import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Loader2, X } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { AppShell } from "@/components/app-shell/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Card";
import { StatusDot } from "@/components/common/StatusDot";
import { api } from "@/lib/api";
import type { ApiError } from "@/lib/api";
import { fmtTs } from "@/lib/format";

export const Route = createFileRoute("/sites")({
  component: SitesPage,
});

type StatusFilter = "all" | "enabled" | "disabled";

function SitesPage() {
  const qc = useQueryClient();
  const sitesQ = useQuery({ queryKey: ["sites"], queryFn: () => api.listSites() });
  const formsQ = useQuery({ queryKey: ["forms"], queryFn: () => api.listForms() });
  const statsQ = useQuery({ queryKey: ["stats", 7], queryFn: () => api.stats(7) });

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [newOpen, setNewOpen] = useState(false);

  const sites = sitesQ.data?.sites ?? [];
  const forms = formsQ.data?.forms ?? [];
  const stats = statsQ.data?.results ?? [];

  // Forms per site.
  const formsBySite = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of forms) m.set(f.siteKey, (m.get(f.siteKey) ?? 0) + 1);
    return m;
  }, [forms]);

  // 7-day sent per site (sum across this site's forms).
  const sentBySite = useMemo(() => {
    const sentByForm = new Map<string, number>();
    for (const r of stats) {
      if (r.outcome === "sent")
        sentByForm.set(r.site_key, (sentByForm.get(r.site_key) ?? 0) + r.n);
    }
    const m = new Map<string, number>();
    for (const f of forms) {
      m.set(f.siteKey, (m.get(f.siteKey) ?? 0) + (sentByForm.get(f.formKey) ?? 0));
    }
    return m;
  }, [stats, forms]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sites.filter((s) => {
      if (filter === "enabled" && !s.enabled) return false;
      if (filter === "disabled" && s.enabled) return false;
      if (q && !s.name.toLowerCase().includes(q) && !s.siteKey.includes(q)) return false;
      return true;
    });
  }, [sites, search, filter]);

  const updateSite = useMutation({
    mutationFn: (v: { siteKey: string; enabled: boolean }) =>
      api.updateSite(v.siteKey, { enabled: v.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sites"] }),
  });

  const deleteSite = useMutation({
    mutationFn: (siteKey: string) => api.deleteSite(siteKey),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sites"] }),
  });

  return (
    <AppShell title="Sites">
      <PageHeader
        eyebrow="sites"
        title="Sites"
        description="Every domain registered to send mail through this Worker. Manage status, edit, or provision new sites."
        actions={
          <button
            type="button"
            onClick={() => setNewOpen(true)}
            className="inline-flex items-center gap-2 rounded-md bg-ember px-3 py-1.5 text-xs font-semibold text-background ring-1 ring-ember/40 transition-all hover:brightness-110"
          >
            <Plus className="h-3.5 w-3.5" /> New site
          </button>
        }
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder="Search by name or site key…"
            className="h-9 w-full rounded-md border border-border bg-surface/40 pl-9 pr-3 text-xs text-zinc-200 placeholder:text-zinc-500 focus:border-ember/60 focus:outline-none focus:ring-2 focus:ring-ember/20"
          />
        </div>
        <div className="flex gap-2">
          {(["all", "enabled", "disabled"] as StatusFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`rounded-md px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                filter === f
                  ? "bg-surface text-zinc-100 ring-1 ring-white/5"
                  : "text-zinc-400 hover:text-zinc-100"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <Panel className="overflow-hidden">
        <div className="overflow-x-auto">
          {sitesQ.isLoading ? (
            <div className="flex items-center gap-2 px-6 py-8 text-xs text-zinc-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> loading sites…
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-12 text-center text-xs text-zinc-500">
              {sites.length === 0
                ? "No sites yet — click ‘New site’ to provision one."
                : "No sites match this filter."}
            </div>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/5 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="px-6 py-3 font-medium">Site</th>
                  <th className="px-6 py-3 font-medium">Key</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Forms</th>
                  <th className="px-6 py-3 font-medium">Sent (7d)</th>
                  <th className="px-6 py-3 font-medium">Updated</th>
                  <th className="px-6 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr
                    key={s.siteKey}
                    className="border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.03]"
                  >
                    <td className="px-6 py-4">
                      <Link
                        to="/sites/$siteKey"
                        params={{ siteKey: s.siteKey }}
                        className="flex items-center gap-3"
                      >
                        <StatusDot status={s.enabled ? "healthy" : "neutral"} />
                        <span className="text-sm font-medium text-zinc-100 hover:text-ember">
                          {s.name}
                        </span>
                      </Link>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs text-zinc-400">{s.siteKey}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                          s.enabled
                            ? "border-success/30 bg-success/10 text-success"
                            : "border-zinc-700 bg-zinc-800/60 text-zinc-500"
                        }`}
                      >
                        {s.enabled ? "enabled" : "disabled"}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs text-zinc-300">
                      {formsBySite.get(s.siteKey) ?? 0}
                    </td>
                    <td className="px-6 py-4 font-mono text-xs text-zinc-300">
                      {sentBySite.get(s.siteKey) ?? 0}
                    </td>
                    <td className="px-6 py-4 font-mono text-[11px] text-zinc-500">
                      {fmtTs(s.updatedAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <Link
                          to="/forms"
                          search={{ site: s.siteKey } as never}
                          className="rounded border border-zinc-700 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-zinc-500 hover:text-zinc-100"
                        >
                          Forms
                        </Link>
                        <button
                          type="button"
                          onClick={() =>
                            updateSite.mutate({ siteKey: s.siteKey, enabled: !s.enabled })
                          }
                          disabled={updateSite.isPending}
                          className="rounded border border-zinc-700 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-zinc-500 hover:text-zinc-100 disabled:opacity-50"
                        >
                          {s.enabled ? "Disable" : "Enable"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Delete site "${s.name}"? This also deletes its forms. This cannot be undone.`,
                              )
                            ) {
                              deleteSite.mutate(s.siteKey);
                            }
                          }}
                          disabled={deleteSite.isPending}
                          className="rounded border border-ember/40 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-ember hover:bg-ember/10 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Panel>

      {newOpen && <NewSiteDialog onClose={() => setNewOpen(false)} />}
    </AppShell>
  );
}

function NewSiteDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [siteKey, setSiteKey] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | undefined>();

  const create = useMutation({
    mutationFn: () => api.createSite({ siteKey: siteKey.trim(), name: name.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sites"] });
      onClose();
    },
    onError: (e) => {
      const ae = e as ApiError;
      setErr(ae.error ?? "Failed to create site.");
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(undefined);
    if (!/^[a-z0-9-]{3,40}$/.test(siteKey.trim())) {
      setErr("Site key must be [a-z0-9-] 3-40 chars.");
      return;
    }
    if (name.trim().length < 2) {
      setErr("Name is required.");
      return;
    }
    create.mutate();
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        className="fade-up w-full max-w-md rounded-xl border border-border bg-background p-6 ring-1 ring-white/5"
      >
        <div className="mb-5 flex items-start justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              new site
            </div>
            <h3 className="mt-1 font-display text-lg font-semibold text-zinc-100">
              Provision a site
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-zinc-400 hover:bg-surface hover:text-zinc-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          Site key
        </label>
        <input
          value={siteKey}
          onChange={(e) => setSiteKey(e.currentTarget.value.toLowerCase())}
          placeholder="acme-corp"
          autoFocus
          className="mb-4 h-9 w-full rounded-md border border-border bg-surface/40 px-3 font-mono text-xs text-zinc-200 placeholder:text-zinc-500 focus:border-ember/60 focus:outline-none focus:ring-2 focus:ring-ember/20"
        />

        <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          Display name
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          placeholder="Acme Corporation"
          className="mb-4 h-9 w-full rounded-md border border-border bg-surface/40 px-3 text-sm text-zinc-200 placeholder:text-zinc-500 focus:border-ember/60 focus:outline-none focus:ring-2 focus:ring-ember/20"
        />

        {err && (
          <div className="mb-3 rounded-md border border-ember/40 bg-ember/10 px-3 py-2 font-mono text-[11px] text-ember">
            {err}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-zinc-300 hover:bg-surface/70"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-ember px-3 py-1.5 text-xs font-semibold text-background ring-1 ring-ember/40 hover:brightness-110 disabled:opacity-50"
          >
            {create.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Create site
          </button>
        </div>
      </form>
    </div>
  );
}
